import { useState, useEffect, useRef, useCallback } from 'react';
import './RevenueRecognition.scss';

import { TableUtility } from '@utility/dynamic-table';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import { useAuth } from '../../../Hooks/useAuth';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { REVENUE_RECOGNITION } from '../../../Api/resources/endPoint';
import {
  getExportFileName,
  getExportFileNameWithDate,
} from '../../Delivery/utils';

import normalizeTableUtilityResponse from '../../../Utils/tableUtilityResponse';

/* eslint-disable max-lines, max-lines-per-function */

/* -------------------------------------------------------------------------- */
/*                                   Base URL                                 */
/* -------------------------------------------------------------------------- */

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${REVENUE_RECOGNITION}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${REVENUE_RECOGNITION}`;
};

const baseURL = fetchBaseURL();

/* -------------------------------------------------------------------------- */
/*                              Cell Click Handler                            */
/* -------------------------------------------------------------------------- */

const handleCellClick = ({ fieldPath, rowData }) => {
  if (fieldPath !== 'project_name') return;

  const projectId = rowData?.project_id;
  if (!projectId) return;

  const projectDetailsUrl = `/delivery/project-details?projectId=${encodeURIComponent(String(projectId))}`;
  window.open(projectDetailsUrl, '_blank', 'noopener,noreferrer');
};

/* -------------------------------------------------------------------------- */
/*                     Response Handler — Pure Helpers                        */
/* -------------------------------------------------------------------------- */

const normalizeStatus = (statusValue) =>
  String(statusValue || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const extractRawTableConfiguration = (response) => {
  if (Array.isArray(response.data?.tableConfiguration)) {
    return response.data.tableConfiguration;
  }
  if (Array.isArray(response.data?.data?.tableConfiguration)) {
    return response.data.data.tableConfiguration;
  }
  if (Array.isArray(response.tableConfiguration)) {
    return response.tableConfiguration;
  }
  if (Array.isArray(response.tableConfig)) {
    return response.tableConfig;
  }
  return [];
};

const ensureResponseColumn = (rawTableConfiguration, field, header) => {
  if (!rawTableConfiguration.some((col) => col.field === field)) {
    rawTableConfiguration.push({
      field,
      header,
      sortable: true,
      filter: false,
      isGlobalFilter: true,
      width: 150,
    });
  }
};

const buildStatusStyleMap = (rawTableConfiguration) => {
  const statusCol = rawTableConfiguration.find(
    (col) =>
      normalizeStatus(col?.field) === 'status'
      && col?.style
      && typeof col.style === 'object',
  );
  return Object.entries(statusCol?.style || {}).reduce(
    (acc, [statusKey, styleValue]) => {
      if (!styleValue || typeof styleValue !== 'object') return acc;
      const key = normalizeStatus(statusKey);
      if (key) {
        acc[key] = {
          textColor: styleValue.textColor || styleValue.textColour,
          boxColor: styleValue.boxColor || styleValue.backgroundColor,
        };
      }
      return acc;
    },
    {},
  );
};

const applyStatusStyles = (rows = [], statusStyleMap) =>
  rows.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const statusStyle = statusStyleMap[normalizeStatus(row.status)];
    let updatedRow = { ...row };
    if (statusStyle?.textColor || statusStyle?.boxColor) {
      const existingTemplate = row.rowConfiguration?.template || [];
      updatedRow = {
        ...updatedRow,
        rowConfiguration: {
          ...(row.rowConfiguration || {}),
          ...(statusStyle.textColor ? { statusColour: statusStyle.textColor } : {}),
          template: [
            ...existingTemplate.filter((entry) => entry.field !== 'status'),
            {
              field: 'status',
              ...(statusStyle.textColor ? { textColor: statusStyle.textColor } : {}),
              fontWeight: '600',
            },
          ],
        },
      };
    }
    if (Array.isArray(row.children)) {
      updatedRow.children = applyStatusStyles(row.children, statusStyleMap);
    }
    return updatedRow;
  });

const INR_DISPLAY_FIELDS = new Set([
  'initial_estimation_effort',
  'cr_change_efforts',
  'total_efforts_required',
  'allocated_effort_month',
  'current_month_effort',
  'efforts_itd',
  'total_effort_estimated',
  'total_reestimated_cost',
  'total_consumed_cost_itd',
  'current_month_cost',
  'tcv',
  'revenue_recognised_itd',
  'revenue_for_the_month',
  'revenue_in_pm',
  'invoiced_itd',
]);

const INR_FORMATTER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatInrDisplayValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return value;
  }

  const numericValue
    = typeof value === 'number'
      ? value
      : Number(
        String(value)
          .replace(/,/g, '')
          .trim(),
      );

  if (!Number.isFinite(numericValue)) {
    return value;
  }

  return INR_FORMATTER.format(numericValue);
};

const applyInrDisplayFormatting = (rows = []) =>
  rows.map((row) => {
    if (!row || typeof row !== 'object') return row;

    const updatedRow = { ...row };

    INR_DISPLAY_FIELDS.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(updatedRow, field)) {
        updatedRow[field] = formatInrDisplayValue(updatedRow[field]);
      }
    });

    if (Array.isArray(row.children)) {
      updatedRow.children = applyInrDisplayFormatting(row.children);
    }

    return updatedRow;
  });

const applyProjectNameLink = (rows = []) =>
  rows.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const existingConfig = row.rowConfiguration || {};
    const existingOnClick = existingConfig.onClick || [];
    if (existingOnClick.some((entry) => entry.field === 'project_name')) return row;
    return {
      ...row,
      rowConfiguration: {
        ...existingConfig,
        onClick: [...existingOnClick, { field: 'project_name' }],
      },
    };
  });

const extractRawTableData = (response) =>
  response.data?.tableData
  || response.data?.data?.tableData
  || response.tableData
  || response.data?.data
  || [];

const buildPaginationMeta = (response) => {
  const pagination
    = response.data?.pagination
    || response.data?.paginationConfig
    || response.data?.data?.pagination
    || response.data?.data?.paginationConfig;
  return {
    ...(response.meta || {}),
    page: pagination?.page ?? response.meta?.page ?? 1,
    limit: pagination?.limit ?? response.meta?.limit ?? 10,
    total:
      pagination?.total
      ?? pagination?.count
      ?? response.meta?.total
      ?? response.data?.tableData?.length
      ?? response.data?.data?.tableData?.length
      ?? 0,
  };
};

const isMonthFilter = (item) => {
  const template = (item?.template || '').toString().toLowerCase();
  const type = (item?.type || '').toString().toLowerCase();
  const pickerType = (item?.pickerType || '').toString().toLowerCase();
  const displayFormat = (item?.displayFormat || '').toString().toLowerCase();

  return (
    template === 'monthcalendar'
    || template === 'month'
    || type === 'month'
    || pickerType === 'month'
    || displayFormat === 'yyyy-mm'
  );
};

const toMonthCalendarValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return `${value.getMonth() + 1}/${value.getFullYear()}`;
  }

  const normalized = value.toString().trim();
  if (!normalized) return null;

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const [, year, month] = yearMonthMatch;
    return `${Number(month)}/${year}`;
  }

  const monthYearMatch = normalized.match(/^(\d{1,2})[/-](\d{4})$/);
  if (monthYearMatch) {
    const [, month, year] = monthYearMatch;
    return `${Number(month)}/${year}`;
  }

  return normalized;
};

const normalizeMonthYearPart = (yearPart) => {
  const normalizedYear = String(yearPart || '').trim();
  if (!normalizedYear) return null;
  if (normalizedYear.length === 2) return `20${normalizedYear}`;
  return /^\d{4}$/.test(normalizedYear) ? normalizedYear : null;
};

const toApiMonthRangeValue = (value, { useEndOfMonth = false } = {}) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = useEndOfMonth ? new Date(year, month, 0).getDate() : 1;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const normalized = String(value).trim();
  if (!normalized) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const [, year, month] = yearMonthMatch;
    const lastDay = new Date(Number(year), Number(month), 0).getDate();
    return `${year}-${String(Number(month)).padStart(2, '0')}-${String(useEndOfMonth ? lastDay : 1).padStart(2, '0')}`;
  }

  const numericMonthYearMatch = normalized.match(/^(\d{1,2})[/-](\d{2}|\d{4})$/);
  if (numericMonthYearMatch) {
    const [, monthPart, yearPart] = numericMonthYearMatch;
    const year = normalizeMonthYearPart(yearPart);
    const month = Number(monthPart);
    if (!year || Number.isNaN(month) || month < 1 || month > 12) return null;
    const lastDay = new Date(Number(year), month, 0).getDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(useEndOfMonth ? lastDay : 1).padStart(2, '0')}`;
  }

  const monthNameYearMatch = normalized.match(/^([A-Za-z]{3,9})[-/\s](\d{2}|\d{4})$/);
  if (monthNameYearMatch) {
    const [, monthName, yearPart] = monthNameYearMatch;
    const year = normalizeMonthYearPart(yearPart);
    if (!year) return null;
    const parsed = new Date(`${monthName} 1, ${year}`);
    if (Number.isNaN(parsed.getTime())) return null;
    return toApiMonthRangeValue(parsed, { useEndOfMonth });
  }

  const parsedDate = new Date(normalized);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return toApiMonthRangeValue(parsedDate, { useEndOfMonth });
};

const normalizeMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') return item;
          const normalizedTemplate = (item?.template || '').toString().toLowerCase();
          if (normalizedTemplate === 'daterange') return item;
          if (!isMonthFilter(item)) return item;

          return {
            ...item,
            template: 'monthCalendar',
            selectedValue: toMonthCalendarValue(item?.selectedValue ?? item?.value ?? null),
          };
        })
        : menuItem?.items,
    }))
    : []
);

/* -------------------------------------------------------------------------- */
/*                          Static Configuration Helpers                      */
/* -------------------------------------------------------------------------- */

const buildConfiguration = ({
  userProfile,
  handleFilteredApiResponse,
  handleMonthFilterUpdate,
  selectedMonthRangeRef,
  tableScrollHeight,
}) => ({
  generalSettings: {
    enableApiFiltering: true,
    baseURL,
    removecommaseperation: true,
    exportHeaderUnmerge: true,
    userInfo: {
      emailId: userProfile.email,
      permissions: userProfile.permissions,
      roleDetails: userProfile.roleDetails,
      employeeId: userProfile.employeeId,
      globalColors: userProfile?.siteConfiguration?.global_colors,
    },
    apiEndpoints: {
      getAllViews: { endpoint: 'views', enabled: true },
      getFilteredData: { endpoint: '', enabled: true },
      saveOrUpdateView: { endpoint: 'view', enabled: true },
      deleteView: { endpoint: 'view-delete', enabled: false },
      download: {
        endpoint: 'export',
        enabled: true,
        filename: () => getExportFileName('Revenue Recognition'),
        sheetName: () => getExportFileNameWithDate('Revenue Recognition'),
      },
    },
    handler: {
      onCellClickHandler: handleCellClick,
      handleFilteredApiResponse,
      onFilterUpdate: handleMonthFilterUpdate,
    },
    additionalPayloadData: {
      get fromMonthYear() {
        return selectedMonthRangeRef.current.toMonthYear;
      },
      get toMonthYear() {
        return selectedMonthRangeRef.current.toMonthYear;
      },
    },

    headers: { token: userProfile.access_token },
    showToolBar: true,
    showPagination: true,
    showTieredMenu: true,
    toolBarSettings: {
      showAddNew: false,
      showDownloadExcel: true,
      showColumnCustomize: false,
      showHelp: false,
      showSearch: false,
      showToolBarHeading: true,
      toolBarHeading: 'Revenue Recognition Report for FP projects',
      showSaveView: false,
      showReset: true,
      showExpandCollapse: false,
      showFilterOption: true,
    },
  },
  showFilterOption: true,
  tableSettings: {
    size: 'small',
    pageSize: 10,
    loaderSkeleton: { rows: 10, bodyCells: 2, columns: 5 },
    paginationConfig: {
      page: 1,
      limit: 10,
      rowsPerPageOptions: [10, 20, 30, 40],
    },
    showExpandCollapse: false,
    tableScrollHeight,
    maxFrozenColumns: { desktop: 2, tablet: 2, mobile: 1 },
  },
});

/* -------------------------------------------------------------------------- */
/*                          Revenue Recognition Component                     */
/* -------------------------------------------------------------------------- */

const getCurrentMonthEndDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
};

const getCurrentMonthCalendarValue = () => {
  const now = new Date();
  return `${now.getMonth() + 1}/${now.getFullYear()}`;
};

const MONTH_FILTER_COLUMN = {
  field: 'selectedMonth',
  header: 'Month & Year',
  width: 150,
  filter: true,
  visible: false,
  sortable: false,
  pinnable: false,
  defaultPinned: false,
  isGlobalFilter: true,
  showFilterButtonInHeader: false,
  showPinButtonInHeader: false,
  menuItems: [
    {
      label: 'Month & Year',
      items: [
        {
          type: 'filter',
          field: 'selectedMonth',
          label: 'Month & Year',
          template: 'monthCalendar',
          placeholder: 'Select Month and Year',
          selectedValue: getCurrentMonthCalendarValue(),
          value: getCurrentMonthCalendarValue(),
        },
      ],
    },
  ],
};

// Module-level helpers (extracted from component to reduce function size)
const applyBodyCellHoverTitles = () => {
  const bodyCells = document.querySelectorAll(
    '.table-utility .p-datatable-tbody > tr > td',
  );

  bodyCells.forEach((td) => {
    const contentElement
      = td.querySelector('.cell-wrapper .ellipses')
        || td.querySelector('.cell-wrapper > span')
        || td.querySelector('span')
        || td.querySelector('div')
        || td;

    const fullText = (contentElement.textContent || '').trim();
    const isTruncated = contentElement.scrollWidth > contentElement.clientWidth;

    if (fullText && isTruncated) {
      td.setAttribute('title', fullText);
      contentElement.setAttribute('title', fullText);
    } else {
      td.removeAttribute('title');
      if (contentElement !== td) {
        contentElement.removeAttribute('title');
      }
    }
  });
};

const applyNumericAlignment = () => {
  const table = document.querySelector('.table-utility .p-datatable');
  if (!table) return;

  const headers = table.querySelectorAll('.p-datatable-thead th');
  const numericColumnIndices = [];

  const numericHeaderPatterns = [
    /master customer id/,
    /sow initial estimation/,
    /cr (change|efforts?)/,
    /total contract efforts/, // Total Contract Efforts Required
    /allocated effort/, // Allocated Effort for the Month
    /current month effort/, // Current Month Effort (Hrs)
    /actual efforts itd/, // Actual Efforts ITD
    /total effort estimated/, // Total Effort Estimated
    /estimated deviation/, // Estimated Deviation (%)
    /re.?estimated cost/, // Total Re-estimated Cost
    /consumed cost/, // Total Consumed Cost (ITD)
    /current month cost/, // Current Month Cost
    /progress\s*%/, // Progress % (ITD), Past Month Progress %, Current Month Progress %
    /\btcv\b/, // TCV
    /\brevenue\b/, // Revenue related columns
    /invoic/, // Invoice / Invoiced
    /\bagm\b/, // AGM (%)
  ];

  headers.forEach((header, index) => {
    const headerText = (header.textContent || '').toLowerCase();
    for (let idx = 0; idx < numericHeaderPatterns.length; idx += 1) {
      if (numericHeaderPatterns[idx].test(headerText)) {
        numericColumnIndices.push(index + 1); // nth-child is 1-based
        break;
      }
    }
  });

  // Clear previous numeric alignments
  const allCells = table.querySelectorAll('.p-datatable-tbody tr td, .p-datatable-thead tr th');
  allCells.forEach((cell) => {
    cell.classList.remove('numeric-aligned');
    cell.style.textAlign = '';
    cell.style.paddingRight = '';

    const div = cell.querySelector('div');
    if (div) {
      div.style.textAlign = '';
      div.style.paddingRight = '';
    }
  });

  // Apply alignment to numeric columns
  numericColumnIndices.forEach((index) => {
    // Body cells
    const bodyCells = table.querySelectorAll(`.p-datatable-tbody tr td:nth-child(${index})`);
    bodyCells.forEach((cell) => {
      cell.style.textAlign = 'right';
      cell.style.paddingRight = '';
      cell.classList.add('numeric-aligned');

      const div = cell.querySelector('div');
      if (div) {
        div.style.textAlign = 'right';
        div.style.paddingRight = '';
      }
    });

    // Header cells
    const headerCells = table.querySelectorAll(`.p-datatable-thead tr th:nth-child(${index})`);
    headerCells.forEach((header) => {
      header.style.textAlign = 'right';
      header.style.paddingRight = '';
      header.classList.add('numeric-aligned');

      const headerContent = header.querySelector('.p-column-header-content');
      if (headerContent) {
        headerContent.style.justifyContent = 'flex-end';
        headerContent.style.textAlign = 'right';
      }
      const headerCol = header.querySelector('.header-column');
      if (headerCol) {
        headerCol.style.justifyContent = 'flex-end';
        headerCol.style.textAlign = 'right';
      }
    });
  });
};

const RevenueRecognition = () => {
  const { userProfile } = useAuth();

  const [tableScrollHeight, setTableScrollHeight] = useState('400px');
  const containerRef = useRef(null);
  const selectedMonthRangeRef = useRef({ fromMonthYear: null, toMonthYear: getCurrentMonthEndDate() });

  const tableConfigInjected = useRef(false);

  const setSelectedMonthRange = useCallback((nextRange) => {
    const toMonth = nextRange?.toMonthYear || nextRange?.fromMonthYear || null;
    selectedMonthRangeRef.current = { fromMonthYear: toMonth, toMonthYear: toMonth };
  }, []);

  const handleMonthFilterUpdate = useCallback(({ action, filters } = {}) => {
    if (action === 'reset') {
      setSelectedMonthRange({ fromMonthYear: null, toMonthYear: null });
      tableConfigInjected.current = false;
      return;
    }
    const monthFilter = (filters || []).find((filter) => filter.field === 'selectedMonth');
    const rawVal = monthFilter?.selectedValue || monthFilter?.value || null;
    const apiVal = toApiMonthRangeValue(rawVal, { useEndOfMonth: true });
    setSelectedMonthRange({ fromMonthYear: apiVal, toMonthYear: apiVal });
  }, [setSelectedMonthRange]);

  // eslint-disable-next-line max-lines-per-function
  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;

    let rawTableConfiguration = extractRawTableConfiguration(response);

    ensureResponseColumn(rawTableConfiguration, 'country', 'Project Country');
    ensureResponseColumn(rawTableConfiguration, 'project_manager', 'Project Manager');
    ensureResponseColumn(rawTableConfiguration, 'senior_manager', 'Senior Manager');
    ensureResponseColumn(rawTableConfiguration, 'delivery_manager', 'Delivery Manager');

    const EXCLUDED_COLUMNS = new Set([
      'project_manager_name', 'senior_manager_name', 'delivery_manager_name',
      'selectedMonth', 'filter_monthYear', 'toMonthYear', 'fromMonthYear',
    ]);
    rawTableConfiguration = rawTableConfiguration.filter(
      (col) => !EXCLUDED_COLUMNS.has(col.field),
    );
    // Prepend a clean single-input monthCalendar filter matching Staffing MIS
    rawTableConfiguration.unshift(MONTH_FILTER_COLUMN);

    const statusStyleMap = buildStatusStyleMap(rawTableConfiguration);
    const tableDataWithStatusStyles
      = applyStatusStyles(extractRawTableData(response), statusStyleMap);
    const tableDataWithDisplayFormatting
      = applyInrDisplayFormatting(tableDataWithStatusStyles);
    const tableDataWithProjectLink
      = applyProjectNameLink(tableDataWithDisplayFormatting);

    // Only inject tableConfiguration on the first call so the table utility
    // keeps its own filter-selection state on subsequent calls.
    const shouldInjectConfig
      = !tableConfigInjected.current && rawTableConfiguration.length > 0;
    const tableConfigUpdate = shouldInjectConfig
      ? {
        tableConfiguration: rawTableConfiguration.map((column) => ({
          ...column,
          menuItems: normalizeMenuItems(Array.isArray(column?.menuItems) ? column.menuItems : []),
        })),
      }
      : {};

    if (shouldInjectConfig) {
      tableConfigInjected.current = true;
    }

    // Strip raw tableConfiguration from the response so subsequent calls never
    // let the unfiltered API config override the cleaned config from the first call.
    if (!shouldInjectConfig) {
      delete response.data?.tableConfiguration;
      delete response.data?.data?.tableConfiguration;
    }

    response.data = {
      ...response.data,
      ...tableConfigUpdate,
      tableData: tableDataWithProjectLink,
      data: {
        ...(response.data?.data || {}),
        tableData: tableDataWithProjectLink,
      },
    };

    response.meta = buildPaginationMeta(response);
    normalizeTableUtilityResponse(response);
  }, []);

  const configuration = buildConfiguration({
    userProfile,
    handleFilteredApiResponse,
    handleMonthFilterUpdate,
    selectedMonthRangeRef,
    tableScrollHeight,
  });


  useEffect(() => {
    const timer = setTimeout(() => {
      const tds = document.querySelectorAll('td[style*="border-left: none"]');

      tds.forEach((td) => {
        const icon = td.querySelector('.pi.pi-angle-right');
        if (icon) {
          icon.style.paddingLeft = '4px';
        }
      });

      applyBodyCellHoverTitles();
    }, 500);

    const tableContainer = document.querySelector('.table-utility');
    const observer = new MutationObserver(() => applyBodyCellHoverTitles());

    if (tableContainer) {
      observer.observe(tableContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const handleResize = () => {
      setTableScrollHeight(
        `${Math.floor(window.innerHeight * 0.7)}px`,
      );
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Right-align numeric/amount columns (Master Customer ID, Initial Estimation Effort, CR Efforts)
  useEffect(() => {
    const timer = setTimeout(applyNumericAlignment, 300);

    const observer = new MutationObserver((mutations) => {
      let shouldReapply = false;
      mutations.forEach((mutation) => {
        const isAttributeChange = mutation.type === 'attributes'
          && ['class', 'style', 'data-p-frozen-column'].includes(mutation.attributeName);
        if (mutation.type === 'childList' || isAttributeChange) {
          shouldReapply = true;
        }
      });
      if (shouldReapply) setTimeout(applyNumericAlignment, 100);
    });

    const tableContainer = document.querySelector('.table-utility');
    if (tableContainer) {
      const observerOptions = {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'data-p-frozen-column'],
      };
      observer.observe(tableContainer, observerOptions);
    }

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="revenue-recognition">
      <div className="table-utility" ref={containerRef}>
        <TableUtility
          configuration={configuration}
        />
      </div>
    </div>
  );
};

export default RevenueRecognition;
